import React, { useRef } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const ListOfStudentReport = ({ 
  isOpen, 
  onClose, 
  selectedCampusId,
  selectedClassId,
  selectedSectionId,
  campusName,
  className,
  sectionName
}) => {
  const reportRef = useRef();
  
  // Get data from Redux store
  const { studentProfileReports, loading } = useSelector(
    (state) => state.studentProfileReport
  );

  // Transform API response to match the expected format
  const transformStudentData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    return apiData.map(student => ({
      id: student.enrollmentNo || student.id,
      fullName: student.studentName,
      gender: student.gender,
      dateOfBirth: new Date(student.dob).toLocaleDateString('en-GB'),
      onboardingDate: new Date(student.admissionDate).toLocaleDateString('en-GB'),
      parentName: student.guardianDetail?.guardianDetail?.fatherName || "N/A",
      parentContact: student.guardianDetail?.guardianDetail?.fatherPhone || "N/A",
      parentCNIC: student.guardianDetail?.guardianDetail?.fatherCNIC || "N/A",
      address: student.location || "N/A",
      status: student.userStatus,
      email: student.email || "N/A",
      phone: student.phone || "N/A",
      cnic: student.cnic || "N/A",
      profilePicture: student.profilePicture,
      sectionName: student.sectionName,
      className: student.className,
      campusName: student.name,
      sessionStart: student.startedSession ? new Date(student.startedSession).toLocaleDateString('en-GB') : "N/A",
      sessionEnd: student.endSession ? new Date(student.endSession).toLocaleDateString('en-GB') : "N/A",
      assignedDate: student.assignedDate ? new Date(student.assignedDate).toLocaleDateString('en-GB') : "N/A"
    }));
  };

  // Use transformed data
  const students = transformStudentData(studentProfileReports);

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "List_of_Students_Report.pdf", 2);
  };

  const downloadExcel = () => {
    if (!students || students.length === 0) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["List of Students Report Summary"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      ["Total Students", students.length],
      [],
      ["Student ID", "Full Name", "Gender", "Date of Birth", "Class/Grade/Section", "Onboarding Date", "Parent/Guardian Name", "Parent Contact", "Address", "Status"]
    ];

    // Add student data
    students.forEach(student => {
      summaryData.push([
        student.id,
        student.fullName,
        student.gender,
        student.dateOfBirth,
        `${className}/${sectionName}`,
        student.onboardingDate,
        student.parentName,
        student.parentContact,
        student.address,
        student.status
      ]);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Student List");

    // Individual student details sheets
    students.forEach((student, index) => {
      const studentData = [
        ["Student Details"],
        ["Student ID", student.id],
        ["Full Name", student.fullName],
        ["Gender", student.gender],
        ["Date of Birth", student.dateOfBirth],
        ["Class/Grade/Section", `${className}/${sectionName}`],
        ["Onboarding Date", student.onboardingDate],
        ["Parent/Guardian Name", student.parentName],
        ["Parent Contact", student.parentContact],
        ["Address", student.address],
        ["Status", student.status],
        ["Campus", campusName],
        ["Report Generated", new Date().toLocaleDateString('en-GB')]
      ];

      const studentSheet = XLSX.utils.aoa_to_sheet(studentData);
      const sheetName = student.fullName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, studentSheet, sheetName);
    });

    XLSX.writeFile(workbook, "List_of_Students_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#241763]"></div>
            <p className="mt-4 text-gray-600">Loading student profile reports...</p>
          </div>
        ) : (
          <>
            {/* Report Header */}
            <div className="text-center mb-8 border-b pb-4">
              <h1 className="text-2xl font-bold text-[#241763] mb-2">List of Students Report</h1>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-left">
                  <p><strong>Campus:</strong> {campusName}</p>
                  <p><strong>Class:</strong> {className}</p>
                </div>
                <div className="text-right">
                  <p><strong>Section:</strong> {sectionName}</p>
                  <p><strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}</p>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                <strong>Total Students:</strong> {students.length}
              </p>
            </div>

        {/* Individual Student Pages */}
        {students && students.length > 0 ? (
          students.map((student, index) => (
            <div key={student.id} style={{ pageBreakAfter: index < students.length - 1 ? 'always' : 'auto' }}>
              {/* Individual Student Report */}
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                  <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
                  <h2 className="text-lg font-semibold mt-2">Student Information Report</h2>
                  <p className="text-base">{new Date().getFullYear()}</p>
                </div>

                {/* Student Info */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Student ID</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{student.id}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Full Name</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{student.fullName}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Gender</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{student.gender}</p>
                      <p className="w-16 font-semibold ml-8">Status</p>
                      <p className="w-32 border-b border-gray-400 ml-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          student.status.toLowerCase() === "active" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-red-100 text-red-800"
                        }`}>
                          {student.status}
                        </span>
                      </p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Date of Birth</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{student.dateOfBirth}</p>
                      <p className="w-16 font-semibold ml-8">Date</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{new Date().toLocaleDateString('en-GB')}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Class/Section</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{className}/{sectionName}</p>
                      <p className="w-28 font-semibold ml-8">Onboarding</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{student.onboardingDate}</p>
                    </div>
                  </div>
                </div>

                {/* Parent/Guardian Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#241763]">Parent/Guardian Information</h3>
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Parent Name</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{student.parentName}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Contact</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{student.parentContact}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Address</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{student.address}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#241763]">Additional Information</h3>
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Campus</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{campusName}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Academic Year</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">2023-24</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Report Type</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">Student Information Report</p>
                    </div>
                  </div>
                </div>

                {/* Signature Lines */}
                <div className="grid grid-cols-3 gap-8 mt-12">
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Coordinator's Signature
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Headmistress's Signature
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Principal's Signature
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 text-lg">No students found for the selected criteria.</p>
          </div>
        )}

            {/* Download Buttons */}
            <div className="no-print flex justify-end mt-6 space-x-4">
              <button 
                onClick={downloadPDF} 
                className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors"
                disabled={loading || students.length === 0}
              >
                Download as PDF
              </button>
              <button 
                onClick={downloadExcel} 
                className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors"
                disabled={loading || students.length === 0}
              >
                Download as Excel
              </button>
              <button 
                onClick={onClose} 
                className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ListOfStudentReport;
